// =============================================================================
// Liveness policy for piece-work face capture — PURE, no React, no I/O.
//
// A face match on the capture station credits PAY. Whether it must first pass
// the blink/turn challenge is a per-DEVICE setting a manager holds on the
// server (clock_devices.liveness_mode, mig 20261023030000); this module is the
// one place that turns that setting plus a match into "challenge or bank".
// Spec: docs/specs/packhouse-piecework-capture.md §13.
//
// CONFIDENT is the attendance kiosk's rule, with the kiosk's numbers: a match
// that is both very close AND clearly separated from the runner-up banks
// without a challenge, because that is the band a spoof does not sit in —
// every marginal AUTO match and the whole CONFIRM band are still challenged.
// The rule and the numbers are imported from faceRecognition.ts, where the
// kiosk reads them too — one copy, so a recalibration cannot leave one screen
// behind.
// =============================================================================

import { FAST_CLOCK_MAX_DISTANCE, FAST_CLOCK_MIN_GAP, qualifiesForFastClock } from '@/lib/faceRecognition'

export type LivenessMode = 'ALWAYS' | 'CONFIDENT' | 'OFF'

export const LIVENESS_MODES: readonly LivenessMode[] = ['ALWAYS', 'CONFIDENT', 'OFF']

/** Anything that is not one of the three — an old cached profile, a null from
 *  a device that failed the gate, a typo — is OFF. Liveness is off for everyone
 *  unless a manager opts a device in (Jason, 14 Oct 2026, reversing the earlier
 *  "the safe default is the one that challenges"): the challenge was too slow
 *  at shift start, so the default must never switch it back on by accident. */
export function parseLivenessMode(raw: unknown): LivenessMode {
  return raw === 'ALWAYS' || raw === 'CONFIDENT' ? raw : 'OFF'
}

// The confidence-gated skip is the kiosk's rule and the kiosk's numbers — ONE
// copy, in faceRecognition.ts beside the bands it is tighter than. Re-exported
// so the policy's callers and tests read it from here.
export { FAST_CLOCK_MAX_DISTANCE, FAST_CLOCK_MIN_GAP, qualifiesForFastClock }

/** Which decision band the matcher put the face in. AUTO = one worker, close
 *  enough to bank silently; CONFIRM = recognised but the foreman must tap WHO. */
export type DecisionBand = 'AUTO' | 'CONFIRM'

/** The whole policy in one question. */
export function challengeRequired(input: {
  mode:     LivenessMode
  band:     DecisionBand
  distance: number
  runnerUp: number | null
}): boolean {
  switch (input.mode) {
    case 'OFF':       return false
    case 'CONFIDENT': return input.band !== 'AUTO' || !qualifiesForFastClock(input.distance, input.runnerUp)
    default:          return true
  }
}

/** Operator-facing words for the three modes — the Devices editor and the
 *  device card use the same ones, so a manager reads one vocabulary. */
export const LIVENESS_MODE_LABEL: Record<LivenessMode, string> = {
  ALWAYS:    'Always challenge',
  CONFIDENT: 'Skip for confident matches',
  OFF:       'Off',
}

export const LIVENESS_MODE_HELP: Record<LivenessMode, string> = {
  ALWAYS:    'Every face match must blink or turn before it clocks or credits piece-work. Slowest; strongest against a photo held to the camera.',
  CONFIDENT: 'A very confident, unambiguous match goes through without the blink/turn step; anything marginal is still challenged.',
  OFF:       'No blink/turn step — the default. A photo held to the camera could clock or credit someone, so use it where a supervisor can see the device.',
}

// ── What the server says about a device, in one shape ───────────────────────
// Shared by the hook (faceScan.tsx) and the offline prewarm task, so the cache
// holds ONE shape under the key: the gate, and the mode already parsed.
export interface PieceworkDeviceProfile { allowed: boolean; liveness_mode: LivenessMode }
export const NO_DEVICE: PieceworkDeviceProfile = { allowed: false, liveness_mode: 'OFF' }

export function shapePieceworkDeviceProfile(raw: unknown): PieceworkDeviceProfile {
  const row = (raw && typeof raw === 'object' ? raw : {}) as { allowed?: unknown; liveness_mode?: unknown }
  return { allowed: row.allowed === true, liveness_mode: parseLivenessMode(row.liveness_mode) }
}

// ── What a face credit carries about its liveness, and how it reads ─────────
/** Recorded on every FACE IdentMeta: the mode the attempt ran under and
 *  whether a blink/turn challenge actually ran. Both screens label a credit
 *  from this, so a confident skip and a device with the check OFF never read
 *  the same. */
export interface FaceLiveness { mode: LivenessMode; challenged: boolean }

export function faceCreditLabel(l: FaceLiveness | undefined): string {
  if (!l || l.challenged) return 'Face'
  return l.mode === 'OFF' ? 'Face · liveness off' : 'Face · confident, no challenge'
}

